import Cookies from 'js-cookie';
import { sessionStorage } from 'utils/storage';
import { RouterLocation } from 'router/location';
import { TProfilesQuery } from './GetProfiles.generated';

export const MAX_PROFILES = 5;
export const PROFILE_AVATAR_SIZE = '9rem';
export const PROFILE_AVATAR_XS_SIZE = '7.5rem';
export const SELECTED_PROFILE_SESSION_KEY = 'selectedProfileId';
export const PROFILE_BROADCAST_CHANNEL = 'profile-selection';
export const PROFILE_MESSAGE_TYPES = {
  profileSelected: 'PROFILE_SELECTED',
  requestProfile: 'REQUEST_SELECTED_PROFILE',
  profileActivityRecorded: 'PROFILE_ACTIVITY_RECORDED',
};
export const PROFILE_LAST_ACTIVITY_STORAGE_KEY = 'profileLastActivityAt';
export const RESELECT_PROFILE_INACTIVE_TIME = 30 * 60 * 1000;
export const PROFILE_ACTIVITY_INTERVAL = 2 * 60 * 1000;
export const PROFILE_ACTIVITY_COOKIE_LIFETIME_DAYS = 30;
const PROFILE_DIALOG_ALLOWED_ROUTE_NAMES = new Set([
  'home',
  'epg',
  'collection',
  'collections',
  'search',
  'genres',
  'watch',
  'webview',
  'collectionWebview',
]);

export const PROFILE_ROUTE_NAMES = [
  'profiles',
  'profiles-edit',
  'profile-edit',
  'profile-new',
  'profile-new-edit',
  'profile-avatar',
];

export const getProfileBroadcastChannelName = (partner?: string): string => {
  const envSuffix = process.env.NODE_ENV === 'production' ? 'prod' : 'dev';
  const partnerSegment = partner ? `-${partner}` : '';
  return `${PROFILE_BROADCAST_CHANNEL}${partnerSegment}-${envSuffix}`;
};

export function recordProfileActivityTimestamp(timestamp: number = Date.now()) {
  try {
    Cookies.set(PROFILE_LAST_ACTIVITY_STORAGE_KEY, String(timestamp), {
      expires: PROFILE_ACTIVITY_COOKIE_LIFETIME_DAYS,
    });
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('Failed to persist profile activity timestamp', error);
    }
  }
}

export function persistSelectedProfileId(profileId: string | undefined) {
  if (!profileId) {
    return;
  }
  try {
    sessionStorage.setItem(SELECTED_PROFILE_SESSION_KEY, profileId);
    recordProfileActivityTimestamp();
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('Failed to persist selected profile to sessionStorage', error);
    }
  }
}

export function clearSelectedProfileId() {
  try {
    sessionStorage.removeItem(SELECTED_PROFILE_SESSION_KEY);
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('Failed to clear selected profile from sessionStorage', error);
    }
  }
}

export type ProfileType = TProfilesQuery['viewer']['profiles'][number];

// A default profile looks like userId with USR suffix replaced by PRF
export function getDefaultProfileId(userId: string | undefined): string | undefined {
  if (!userId) return undefined;
  return userId.replace(/USR$/, 'PRF');
}

export function isDefaultProfile(profileId: string | undefined, userId: string | undefined): boolean {
  if (!profileId || !userId) return false;
  const expectedDefaultProfileId = getDefaultProfileId(userId);
  return profileId === expectedDefaultProfileId;
}

// Mark profile activity as sufficiently old so that the profile-selection
// logic treats the user as inactive and may prompt for profile selection.
export function markProfileActivityAsOutdated() {
  const outdatedTimestamp = Date.now() - (RESELECT_PROFILE_INACTIVE_TIME + 1000);
  recordProfileActivityTimestamp(outdatedTimestamp);
}

export function clearProfileActivityTimestamp() {
  try {
    Cookies.remove(PROFILE_LAST_ACTIVITY_STORAGE_KEY);
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('Failed to clear profile activity timestamp', error);
    }
  }
}

export function isRouteAllowedForProfilePrompt(routeName: string | undefined): boolean {
  if (!routeName) {
    return false;
  }
  return PROFILE_DIALOG_ALLOWED_ROUTE_NAMES.has(routeName);
}

export function getStoredProfileId(): string | null {
  try {
    return sessionStorage.getItem(SELECTED_PROFILE_SESSION_KEY);
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('Failed to read selected profile from sessionStorage', error);
    }
    return null;
  }
}

export function getLastProfileActivityTimestamp(): number | undefined {
  try {
    const rawValue = Cookies.get(PROFILE_LAST_ACTIVITY_STORAGE_KEY);
    if (!rawValue) return undefined;
    const parsed = Number(rawValue);
    return Number.isFinite(parsed) ? parsed : undefined;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('Failed to read profile activity timestamp', error);
    }
    return undefined;
  }
}

// Returns true if the last recorded profile activity is older than the
// RESELECT_PROFILE_INACTIVE_TIME threshold.
export function isInactiveForProfileReselect(): boolean {
  const lastActivityTimestamp = getLastProfileActivityTimestamp();
  if (typeof lastActivityTimestamp !== 'number') {
    return false;
  }
  return (Date.now() - lastActivityTimestamp) > RESELECT_PROFILE_INACTIVE_TIME;
}

export function cloneRouterLocation(location?: RouterLocation): RouterLocation | undefined {
  if (!location) {
    return undefined;
  }
  return {
    name: location.name,
    ...(location.params ? { params: { ...location.params } } : {}),
    ...(location.query ? { query: { ...location.query } } : {}),
    ...(typeof location.suppressViewChange !== 'undefined'
      ? { suppressViewChange: location.suppressViewChange }
      : {}),
  };
}
